/*
 * Description:
 * ---------------------
 * Remote Devices: a device on one site's network that computers at other sites
 * reach, each site at an address of its own (server/pkg/reach).
 *
 * The table lives on the Weavian server, which hands each site its part on its
 * next check-in. The screen shows one customer at a time, since sharing never
 * crosses a customer: first its master map (MasterMap), every box and what
 * devices at each may connect to at the others, and from it any box's focused
 * view (AccessCanvas), whose Back returns to the map as it was, since the map
 * stays mounted and its open boxes, box panels and pick live here. A customer
 * with at most OPEN_ALL boxes sees every box open at first. Allowing access, and
 * managing who may connect to a device, happen in the access editor
 * (ShareSheet); once saved, the view shown picks the new rule. This panel
 * owns every call to the server: a whole network that takes over devices already shared alone removes
 * them first, and puts them back if the server then refuses the network.
 * Removing a box's access keeps every other box's, and removing the last one
 * stops sharing the device. Every refusal is the server's own sentence. All
 * other wording lives in reachWords.js.
 *
 * ! file components/RemoteDevicesPanel.js
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { isMobileNode } from "../utils/nodeKind";
import { useConfirm } from "./ConfirmModal";
import { useSession } from "./PortalGate";
import AccessCanvas from "./AccessCanvas";
import MasterMap from "./MasterMap";
import ShareSheet from "./ShareSheet";
import * as L from "./mapLayout";
import * as W from "./reachWords";
import "./DeviceLimits.css";
import "./RemoteDevices.css";

const POLL_MS = 10000;
const SWITCH_AS_TABS = 5;
// A customer with at most OPEN_ALL boxes sees them all open at first; more start folded, since each open box reads its devices.
const OPEN_ALL = 8;
const CUSTOMER_KEY = "nabx.remoteDevices.customer";

const remembered = () => {
  try {
    return window.localStorage.getItem(CUSTOMER_KEY) || "";
  } catch {
    return "";
  }
};
const remember = (id) => {
  try {
    window.localStorage.setItem(CUSTOMER_KEY, id);
  } catch {
    // A browser that keeps nothing still shows the customer picked, until reload.
  }
};

export default function RemoteDevicesPanel({ serverApiUrl, nodes }) {
  const session = useSession();
  const confirm = useConfirm();
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [domains, setDomains] = useState([]);
  const [customerId, setCustomerId] = useState(remembered);
  // The map's open boxes, box panels and pick, and the box focused on with its pick.
  const [open, setOpen] = useState(null);
  const [panels, setPanels] = useState([]);
  const [mapPick, setMapPick] = useState(null);
  const [focus, setFocus] = useState(null);
  const [focusPick, setFocusPick] = useState(null);
  const [sheet, setSheet] = useState(null); // { mode: "allow", start } or { mode: "edit", entry }
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl("/reach"), { credentials: "same-origin" }).catch(() => null);
    if (!mounted.current) return;
    if (!res) return setError("Could not reach the Weavian server.");
    if (res.status === 404) return setError(W.NOT_ON_SERVER);
    if (!res.ok) return setError(`The Weavian server answered ${res.status}.`);
    setView(await res.json());
    setError("");
  }, [serverApiUrl]);
  useVisibleInterval(load, POLL_MS);

  useEffect(() => {
    fetchWithTimeout(serverApiUrl("/domains"), { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => mounted.current && d?.domains && setDomains(d.domains))
      .catch(() => {});
  }, [serverApiUrl]);

  // call makes one request and answers with the server's reply, or a sentence.
  const call = async (method, path, body) => {
    const res = await fetchWithTimeout(serverApiUrl(path), {
      method,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }).catch(() => null);
    const data = res ? await res.json().catch(() => ({})) : null;
    if (!res) return { ok: false, why: "Could not reach the Weavian server, so nothing was changed." };
    if (!res.ok) return { ok: false, why: data.error || `The Weavian server refused this (${res.status}).` };
    return { ok: true, data };
  };

  // send makes one change from the list and shows the table as it then stands.
  const send = async (method, path, body) => {
    setBusy(true);
    setFailure("");
    const r = await call(method, path, body);
    if (!mounted.current) return false;
    setBusy(false);
    if (!r.ok) {
      setFailure(r.why);
      return false;
    }
    setView(r.data);
    return true;
  };

  // save applies the sheet: first the entries a whole network takes over, then
  // the entry itself. A refusal puts back whatever was taken over, unchanged;
  // success closes the sheet and picks the new path on the canvas.
  const save = async (plan) => {
    setBusy(true);
    setFailure("");
    const removed = [];
    const undo = async (why) => {
      for (const e of removed) await call("POST", "/reach", W.bodyFromForm(W.formFromDevice(e)));
      if (!mounted.current) return;
      await load();
      setBusy(false);
      setFailure(removed.length ? `${why} Nothing was changed.` : why);
    };
    for (const e of plan.replaces) {
      const r = await call("DELETE", `/reach/${e.id}`);
      if (!r.ok) return undo(r.why);
      removed.push(e);
    }
    const r = await call(plan.method, plan.path, plan.body);
    if (!r.ok) return undo(r.why);
    if (!mounted.current) return;
    setView(r.data);
    setBusy(false);
    setSheet(null);
    const id = r.data.saved || plan.id;
    if (!id || !plan.select) return;
    if (focus) {
      setFocus(plan.select);
      setFocusPick(`${id}>${plan.select}`);
    } else {
      setOpen((o) => (o?.includes(plan.body.site) ? o : [...(o || []), plan.body.site]));
      setPanels((ps) => L.openPanel(ps, plan.body.site, { rule: `${id}>${plan.select}` }));
      setMapPick({ kind: "rule", key: `${id}>${plan.select}` });
    }
  };
  const unpick = () => {
    setMapPick(null);
    setFocusPick(null);
  };

  const toggle = (d) => send("PUT", `/reach/${d.id}`, { ...W.bodyFromForm(W.formFromDevice(d)), disabled: !d.disabled });
  // removeAccess takes one box's access away; the last one stops sharing.
  const removeAccess = async (rule) => {
    const d = rule.entry;
    const last = d.reached_from.length === 1;
    const ok = await confirm({
      title: W.removeTitle(rule.box, d.name),
      message: W.removeMessage(rule.box, last),
      confirmLabel: W.removeAccess(rule.box),
      variant: "danger",
    });
    if (!ok) return;
    const rest = d.reached_from.filter((c) => c.site !== rule.box).map((c) => ({ site: c.site, address: c.address || "" }));
    const done = last
      ? await send("DELETE", `/reach/${d.id}`)
      : await send("PUT", `/reach/${d.id}`, { ...W.bodyFromForm(W.formFromDevice(d)), reached_from: rest });
    if (done) unpick();
  };
  const remove = async (d) => {
    const ok = await confirm({
      title: `Stop sharing ${d.name}?`,
      message: `${d.reached_from.map((c) => c.site).join(", ") || "No site"} will no longer reach it, and connections open now are ended.`,
      confirmLabel: W.STOP_SHARING,
      variant: "danger",
    });
    if (ok && (await send("DELETE", `/reach/${d.id}`))) {
      setSheet(null);
      unpick();
    }
  };

  const allSites = useMemo(
    () => (nodes || []).filter((n) => !isMobileNode(n) && (n.mode || "anchored") !== "direct"),
    [nodes],
  );
  const groups = useMemo(() => W.customerGroups(allSites, domains), [allSites, domains]);
  const everyEntry = useMemo(() => view?.devices || [], [view]);
  const current = useMemo(() => W.pickCustomer(groups, customerId, everyEntry), [groups, customerId, everyEntry]);
  // The boxes open when a customer's map first shows: all of them, unless there are many.
  const firstOpen = useMemo(() => (current && current.sites.length <= OPEN_ALL ? current.sites.map((s) => s.node_id) : []), [current]);
  useEffect(() => {
    if (open === null && current) setOpen(firstOpen);
  }, [open, current, firstOpen]);
  const entries = useMemo(() => {
    const ids = new Set((current?.sites || []).map((s) => s.node_id));
    return everyEntry.filter((e) => ids.has(e.site));
  }, [current, everyEntry]);

  if (!view) {
    return <div className="panel dl-note">{error || "Reading the remote devices…"}</div>;
  }

  const sites = current?.sites || [];
  const openSheet = (next) => {
    setFailure("");
    setSheet(next);
  };
  const pickCustomer = (id) => {
    setCustomerId(id);
    remember(id);
    setOpen(null);
    setPanels([]);
    unpick();
    setFocus(null);
  };
  const shared = { sites, entries, view, canWrite: session.canWrite, busy, onManage: (entry) => openSheet({ mode: "edit", entry }),
    onToggle: toggle, onRemoveAccess: removeAccess };
  return (
    <div className="dl rd">
      {!session.canWrite && <p className="dl-note">{W.READ_ONLY}</p>}
      {error && <p className="dl-error">{error}</p>}
      {failure && !sheet && <p className="dl-error" role="alert">{failure}</p>}
      {groups.length > 1 && <CustomerSwitch groups={groups} current={current} onChange={pickCustomer} />}
      {!sites.length && <p className="panel dl-note">{W.NO_BOXES}</p>}
      {sites.length > 0 && (
        <div hidden={!!focus}>
          <MasterMap key={current.id} group={current} {...shared} open={open ?? firstOpen} onOpen={setOpen} panels={panels} onPanels={setPanels}
            selection={mapPick} onSelection={setMapPick}
            onFocus={(id) => {
              setFocus(id);
              setFocusPick(null);
            }}
            onAllow={(start) => openSheet({ mode: "allow", start })} />
        </div>
      )}
      {focus && sites.some((s) => s.node_id === focus) && (
        <AccessCanvas group={current} {...shared} source={focus} selected={focusPick} onSelect={setFocusPick}
          onSource={(id) => {
            setFocus(id);
            setFocusPick(null);
          }}
          onBack={() => setFocus(null)} onAllow={() => openSheet({ mode: "allow", start: { source: focus } })} />
      )}
      {sheet && (
        <ShareSheet key={`${sheet.mode}:${sheet.entry?.id || JSON.stringify(sheet.start)}`} {...sheet} sites={sites} entries={entries}
          view={view} busy={busy} failure={failure} onSave={save} onRemove={remove} onClose={() => !busy && openSheet(null)} />
      )}
    </div>
  );
}

function CustomerSwitch({ groups, current, onChange }) {
  if (groups.length > SWITCH_AS_TABS) {
    return (
      <label className="rd-customer">
        <span>{W.CUSTOMER_LABEL}</span>
        <select className="dl-input" value={current?.id || ""} onChange={(e) => onChange(e.target.value)} aria-label={W.CUSTOMER_LABEL}>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.sites.length})</option>)}
        </select>
      </label>
    );
  }
  return (
    <div className="rd-customer" role="group" aria-label={W.CUSTOMER_LABEL}>
      <span>{W.CUSTOMER_LABEL}</span>
      <div className="rd-tabs">
        {groups.map((g) => (
          <button key={g.id} type="button" className={`rd-tab${g.id === current?.id ? " is-on" : ""}`}
            aria-pressed={g.id === current?.id} onClick={() => onChange(g.id)}>
            {g.name}<em>{g.sites.length}</em>
          </button>
        ))}
      </div>
    </div>
  );
}
